/**
 * orgPicker — pure view-model helpers for the post-login "Select an
 * organization" screen, shared by the web picker
 * (apps/web/src/auth/OrgSelectScreen.tsx) and the iOS picker
 * (apps/mobile/src/screens/auth/OrgSelectScreen.tsx) so the two never
 * drift on grouping, relative-time wording, or search matching.
 */

import type { Organization } from './index'

/** One stake and the wards grouped under it, as rendered in a single card. */
export interface OrgGroup {
  /** Parent stake org id, or null for wards with no resolvable stake. */
  stakeId: string | null
  /** Card header label — the stake's name, or a generic fallback. */
  stakeName: string
  /** The stake's own org row, if the user has access to it (else null). */
  stake: Organization | null
  /** Wards under this stake, alphabetical by name. */
  wards: Organization[]
}

export interface GroupedOrgs {
  groups: OrgGroup[]
  /** Sandbox / training orgs (isDemo), pulled out of the live groups. */
  demo: Organization[]
}

const NO_STAKE_KEY = '__nostake__'
const FALLBACK_STAKE_NAME = 'Your organizations'

/**
 * Split the user's orgs into (a) live stake groups and (b) demo orgs.
 *
 * Live orgs are grouped by their parent stake: a `stake` org keys its
 * group by its own id and supplies the header name; a `ward` keys by
 * `stakeId`. When the parent stake itself isn't in the user's list (e.g.
 * a multi-ward leader who isn't a stake member) we can't read its name,
 * so the header falls back to a generic label.
 */
export function groupOrganizations(orgs: Organization[]): GroupedOrgs {
  const demo = orgs.filter((o) => o.isDemo === true)
  const live = orgs.filter((o) => o.isDemo !== true)

  const byKey = new Map<string, OrgGroup>()
  const ensure = (key: string): OrgGroup => {
    let g = byKey.get(key)
    if (!g) {
      g = { stakeId: key === NO_STAKE_KEY ? null : key, stakeName: '', stake: null, wards: [] }
      byKey.set(key, g)
    }
    return g
  }

  for (const org of live) {
    if (org.type === 'stake') {
      const g = ensure(org.id)
      g.stake = org
      g.stakeName = org.name
    } else {
      const g = ensure(org.stakeId || NO_STAKE_KEY)
      g.wards.push(org)
    }
  }

  const groups = [...byKey.values()]
  for (const g of groups) {
    g.wards.sort((a, b) => a.name.localeCompare(b.name))
    if (!g.stakeName) g.stakeName = FALLBACK_STAKE_NAME
  }

  // Groups with a real stake header first, then alphabetically; the
  // generic fallback bucket sorts last.
  groups.sort((a, b) => {
    const af = a.stakeName === FALLBACK_STAKE_NAME
    const bf = b.stakeName === FALLBACK_STAKE_NAME
    if (af !== bf) return af ? 1 : -1
    return a.stakeName.localeCompare(b.stakeName)
  })

  return { groups, demo }
}

/**
 * Relative "last updated" string for an org row, matching the design's
 * wording: "Updated today", "Updated 2h ago", "Updated yesterday",
 * "Updated 3d ago", "Updated 1w ago", then an absolute date. Returns ''
 * for a missing/invalid timestamp so callers can hide the line.
 */
export function formatOrgUpdated(iso: string | undefined | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const ms = Math.max(0, Date.now() - d.getTime())
  const hr = Math.floor(ms / 3_600_000)
  if (hr < 24) {
    if (hr < 1) return 'Updated today'
    return `Updated ${hr}h ago`
  }
  const days = Math.floor(hr / 24)
  if (days === 1) return 'Updated yesterday'
  if (days < 7) return `Updated ${days}d ago`
  const weeks = Math.floor(days / 7)
  if (weeks < 5) return `Updated ${weeks}w ago`
  return `Updated ${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}`
}

/**
 * Case-insensitive search predicate shared by both platforms' search
 * boxes — matches an org against its name and its type label
 * ("stake"/"ward"). Empty query matches everything.
 */
export function matchesOrgQuery(org: Organization, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  const typeLabel = org.type === 'stake' ? 'stake' : 'ward'
  return org.name.toLowerCase().includes(q) || typeLabel.includes(q)
}
